import { apiClient } from '../lib/apiClient';

/**
 * Aquarium controller data (#1899): sources (bridges that push readings, e.g.
 * Home Assistant for a GHL ProfiLux), the mapping of each reported key, the
 * reading history and the measured electricity. Backend: src/routes/controller.ts.
 */

export type ControllerSourceStatus = 'active' | 'revoked';

export interface ControllerSource {
  id: string;
  aquariumId: string;
  name: string;
  status: ControllerSourceStatus;
  lastReceivedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

/** Only the create response carries the device token — show it once, never store it. */
export type ControllerSourceWithToken = ControllerSource & { token: string };

export const SENSOR_KINDS = ['unmapped', 'parameter', 'power', 'energy', 'other', 'ignore'] as const;
export type SensorKind = (typeof SENSOR_KINDS)[number];

export const SENSOR_LOCATIONS = ['tank', 'reactor', 'other'] as const;
export type SensorLocation = (typeof SENSOR_LOCATIONS)[number];

export const SENSOR_PARAMETER_IDS = [
  'temperature',
  'ph',
  'salinity',
  'conductivity',
  'orp',
  'oxygen',
  'alkalinity',
  'calcium',
  'magnesium',
  'nitrate',
  'phosphate',
] as const;
export type SensorParameterId = (typeof SENSOR_PARAMETER_IDS)[number];

/**
 * Units of the hand test per parameter (lower case). A key of such a parameter
 * can only record measurements in one of them, so the series never mixes scales
 * (same rule as src/services/sensor/probeUnits.ts). Probe-only parameters
 * (redox, oxygen, conductivity) take any unit.
 */
const TEST_UNITS: Readonly<Partial<Record<SensorParameterId, readonly string[]>>> = {
  temperature: ['°c', 'c'],
  ph: ['', 'ph'],
  salinity: ['ppt', 'psu', '‰'],
  alkalinity: ['dkh', '°dkh'],
  calcium: ['ppm', 'mg/l'],
  magnesium: ['ppm', 'mg/l'],
  nitrate: ['ppm', 'mg/l'],
  phosphate: ['ppm', 'mg/l'],
};

/** True when a tank water key of `parameterId` in `unit` may record measurements. */
export function measurementUnitFits(parameterId: SensorParameterId, unit: string | null): boolean {
  const units = TEST_UNITS[parameterId];
  return units === undefined || units.includes((unit ?? '').trim().toLowerCase());
}

export interface SensorMapping {
  id: string;
  sourceId: string;
  sourceKey: string;
  kind: SensorKind;
  parameterId: SensorParameterId | null;
  label: string | null;
  /** The unit override, else the unit the bridge reported. */
  unit: string | null;
  reportedUnit: string | null;
  /** Device the bridge reported the value from (e.g. "GHL ProfiLux"); null when not sent. */
  origin: string | null;
  scaleFactor: number;
  location: SensorLocation;
  countsTowardCost: boolean;
  /** New values are also saved as measurements (source "controller"); tank water values only. */
  recordAsMeasurement: boolean;
  /** Latest value, scale factor applied. */
  lastValue: number | null;
  lastMeasuredAt: string | null;
}

export interface UpdateSensorMappingInput {
  kind?: SensorKind;
  parameterId?: SensorParameterId | null;
  label?: string | null;
  unit?: string | null;
  scaleFactor?: number;
  location?: SensorLocation;
  countsTowardCost?: boolean;
  recordAsMeasurement?: boolean;
}

export interface SensorReading {
  measuredAt: string;
  windowStart: string | null;
  value: number;
  min: number | null;
  max: number | null;
}

export interface SensorReadingsResponse {
  mapping: SensorMapping;
  readings: SensorReading[];
  /** 'day': ranges over 90 days come as one point per day (average, lowest min, highest max). */
  aggregated: 'none' | 'day';
}

export interface EnergyMonth {
  month: number;
  kwh: number;
  costCents: number | null;
}

export interface EnergySummary {
  year: number;
  /** Cents (of the user's currency) per kWh; null when no price is set. */
  pricePerKwhCents: number | null;
  /** At least one key counts toward the cost. */
  configured: boolean;
  hasData: boolean;
  months: EnergyMonth[];
  totalKwh: number;
  totalCostCents: number | null;
  averageWatts: number | null;
  projectedKwh: number | null;
  projectedCostCents: number | null;
}

interface ApiEnvelope<T> {
  data: T;
}

const base = (aquariumId: string): string => `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/controller`;

/** Path a bridge posts readings to (with the X-Device-Token header). */
export function controllerIngestPath(sourceId: string): string {
  return `/api/v1/controller-sources/${encodeURIComponent(sourceId)}/readings`;
}

export async function listControllerSources(aquariumId: string): Promise<ControllerSource[]> {
  return (await apiClient.get<ApiEnvelope<ControllerSource[]>>(`${base(aquariumId)}/sources`)).data;
}

export async function createControllerSource(aquariumId: string, name: string): Promise<ControllerSourceWithToken> {
  return (await apiClient.post<ApiEnvelope<ControllerSourceWithToken>>(`${base(aquariumId)}/sources`, { name })).data;
}

export async function revokeControllerSource(aquariumId: string, id: string): Promise<ControllerSource> {
  return (await apiClient.post<ApiEnvelope<ControllerSource>>(`${base(aquariumId)}/sources/${encodeURIComponent(id)}/revoke`, {})).data;
}

/** Deletes a REVOKED source with its keys and readings (409 while it is active). */
export async function deleteControllerSource(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete<void>(`${base(aquariumId)}/sources/${encodeURIComponent(id)}`);
}

export async function listSensorMappings(aquariumId: string): Promise<SensorMapping[]> {
  return (await apiClient.get<ApiEnvelope<SensorMapping[]>>(`${base(aquariumId)}/mappings`)).data;
}

export async function updateSensorMapping(aquariumId: string, id: string, input: UpdateSensorMappingInput): Promise<SensorMapping> {
  return (await apiClient.patch<ApiEnvelope<SensorMapping>>(`${base(aquariumId)}/mappings/${encodeURIComponent(id)}`, input)).data;
}

export async function listSensorReadings(aquariumId: string, mappingId: string, days: number): Promise<SensorReadingsResponse> {
  return (
    await apiClient.get<ApiEnvelope<SensorReadingsResponse>>(`${base(aquariumId)}/mappings/${encodeURIComponent(mappingId)}/readings`, {
      params: { days },
    })
  ).data;
}

export async function getEnergySummary(aquariumId: string, year: number): Promise<EnergySummary> {
  return (await apiClient.get<ApiEnvelope<EnergySummary>>(`${base(aquariumId)}/energy`, { params: { year } })).data;
}

export async function setEnergyPrice(aquariumId: string, pricePerKwhCents: number | null): Promise<{ pricePerKwhCents: number | null }> {
  return (await apiClient.put<ApiEnvelope<{ pricePerKwhCents: number | null }>>(`${base(aquariumId)}/energy/settings`, { pricePerKwhCents })).data;
}

/** Target band as the measurement API returns it (min / max and the "stable" margin). */
export interface ProbeTarget {
  min: number;
  max: number;
  margin?: number;
}

/** The latest tank probe value of a water parameter (GET …/controller/probe-values). */
export interface ProbeValue {
  parameterId: string;
  mappingId: string;
  label: string | null;
  origin: string | null;
  unit: string | null;
  value: number;
  /** Min / max of the window of the latest reading. */
  min: number | null;
  max: number | null;
  measuredAt: string;
  /** Target in force — only when the probe's unit is the target's unit. */
  target: ProbeTarget | null;
}

/** The probe readings of one parameter over a measurement-history range. */
export interface ProbeSeries {
  mappingId: string;
  label: string | null;
  origin: string | null;
  unit: string | null;
  aggregated: 'none' | 'day';
  readings: SensorReading[];
}

export async function getLatestProbeValues(aquariumId: string): Promise<ProbeValue[]> {
  return (await apiClient.get<ApiEnvelope<ProbeValue[]>>(`${base(aquariumId)}/probe-values`)).data;
}

/** Probe history of 1–3 parameters for a measurement-history range ('30d' | '90d' | '1y' | 'all'), keyed by parameter id. */
export async function getProbeHistory(
  aquariumId: string,
  range: string,
  parameterIds: readonly string[],
): Promise<Record<string, ProbeSeries>> {
  return (
    await apiClient.get<ApiEnvelope<Record<string, ProbeSeries>>>(`${base(aquariumId)}/probe-history`, {
      params: { range, params: parameterIds.join(',') },
    })
  ).data;
}
